// src/constants/myClipsSchema.js
// MyClips Firestore `clips/{clipId}` doc şema sözleşmesi — tek kaynak.
// Client yazıcılar, sunucu Cloud Function'lar ve Firestore rules hepsi buradan okumalı.
// Faz 1A Görev 3: şema migrasyonu.

import { LADDER, MANUEL_KALITE_DEGERLERI } from "../myclipsv3/shared/ladder";

// ─── STATUS DEĞERLERİ ─────────────────────────────────────────────────────────
export const CLIP_STATUS = Object.freeze({
  // Client sadece PROCESSING yazabilir. READY/FAILED/REJECTED sunucu-only.
  PROCESSING: "processing", // Raw yüklendi, Transcoder işliyor (client set eder)
  READY: "ready",           // Transcode tamam, manifestUrl geçerli (server set eder)
  FAILED: "failed",         // Transcode/moderasyon hata (server set eder)
  REJECTED: "rejected",     // Moderasyon reddetti (SafeSearch vb.) (server set eder)
  // EMİR #99 §4.3 — CANLI DEĞER, ÖLÇÜLDÜ (tam sayım 156 doküman): 128 klip
  // `status:"v3_only"` taşıyor ve bu sabit onu HİÇ İÇERMİYORDU. Dosyanın kendi
  // iddiası "tek kaynak" olduğu için bu bir eksiklik değil, YALANDI: kaynağa
  // bakan biri v3_only'nin var olmadığı sonucuna varırdı — ana akış sorgusunun
  // 128 klibi kapsam dışı bırakmasının (KUSUR A) zemini tam olarak buydu.
  // Yazan: myclipsv3/compose/pipeline/firestoreWrite.ts:119 (client, Phase-1).
  V3_ONLY: "v3_only",
});

// ⚠️ ÖLÇÜM NOTU (EMİR #99 §4.3): aşağıdaki iki liste `src/`, `functions/src/`
// ve `firestore.rules` genelinde HİÇBİR YERDE kullanılmıyor — ölü ihracat.
// `V3_ONLY` bilerek EKLENMEDİ: kullanılmayan bir listeye üye eklemek, o üyenin
// bir yerde YAPTIRIM gördüğü izlenimi verirdi (görmüyor). Listeler ya bir
// yaptırıma bağlanmalı ya kaldırılmalı — ikisi de bu emrin kapsamı dışında,
// ama artık YAZILI: "kullanılmıyor" bir kategori değil, ölçülmüş bir durum.

export const CLIENT_WRITABLE_STATUS = [CLIP_STATUS.PROCESSING];
export const SERVER_ONLY_STATUS = [
  CLIP_STATUS.READY,
  CLIP_STATUS.FAILED,
  CLIP_STATUS.REJECTED,
];

// ─── SOURCE (içerik kaynağı) ──────────────────────────────────────────────────
export const CLIP_SOURCE = Object.freeze({
  USER: "user",             // Son kullanıcı upload'u
  BOT_PEXELS: "bot_pexels", // @mylasa.ai botu — Pexels API
  BOT_PIXABAY: "bot_pixabay", // @mylasa.ai botu — Pixabay API
});

// ─── AUTHOR TYPE ──────────────────────────────────────────────────────────────
export const CLIP_AUTHOR_TYPE = Object.freeze({
  USER: "user",
  BOT: "bot",
});

// ─── ALAN GRUPLARI ────────────────────────────────────────────────────────────
// Client CREATE sırasında yazılması ZORUNLU alanlar
export const CLIP_REQUIRED_ON_CREATE = Object.freeze([
  "authorId",
  "authorType",
  "source",
  "status",
  "tarih",
  "aciklama",
  "rawPath",
  "rawExt",
  "begenenler",
  "yorumlar",
]);

// Client UPDATE'de dokunulamaz alanlar (sunucu-only)
// Cloud Function admin SDK ile yazar — rules bypass eder.
export const CLIP_SERVER_ONLY_FIELDS = Object.freeze([
  "manifestUrl",   // HLS master .m3u8
  "posterUrl",     // Ana poster (ilk kare ya da AI-seçili)
  "thumbUrl",      // Grid thumb (küçük/WebP)
  "durationMs",    // Authoritative süre (Transcoder)
  "aspectRatio",   // Authoritative oran
  "width",         // Authoritative genişlik
  "height",        // Authoritative yükseklik
  "processedAt",   // Transcode bitiş timestamp
  "moderationStatus", // safeSearch sonucu
  "engagementScore",  // Ranking skoru (sunucu hesap)
  "qualityBucket",    // Kalite kovası (sunucu)
  "lastRankedAt",     // Sıralama zamanı
  // Faz 1B Görev 1: İzlenme telemetri alanları — callable Cloud Function yazar.
  "viewCount",        // Toplam unique view
  "completeCount",    // Sonuna kadar izleme sayısı (%90+)
  "watchedMsTotal",   // Toplam izlenme ms (avg_watch_time hesabı için)
  "lastViewAt",       // Son view timestamp
  // FAZ C — Multi-bitrate adaptive streaming variant array (yatırımcı şartı #2).
  // Transcoder pipeline tarafından yazılır (myclips_v3_transcode.ts ya da
  // Cloud Run job sonrası transcoder_callback). Her variant ayrı R2 dosyası;
  // client useVariantSelector ile network downlink'e göre seçer.
  "v3_variants",
  "v3_variantsUpdatedAt",
  // P0.4 — ladder sözleşme sürümü (Cloud Run / Worker callback yazar).
  "v3_ladderVersion",
]);

// Sahibin UPDATE edebileceği alanlar (client)
export const CLIP_OWNER_MUTABLE_FIELDS = Object.freeze([
  "aciklama",
]);

// Herhangi bir kullanıcının UPDATE edebileceği alanlar (etkileşim)
export const CLIP_PUBLIC_MUTABLE_FIELDS = Object.freeze([
  "begenenler",
  "yorumlar", // eski inline yorumlar için geri uyum
]);

// Aciklama maks uzunluk (rules ile uyumlu)
export const CLIP_ACIKLAMA_MAX_LENGTH = 2200;

// ─── KİŞİ ETİKETLEME v1 — taggedUids ──────────────────────────────────────
// Alan: clips/{clipId}.taggedUids — string uid array (max 20).
//   - Composer "Kişi Etiketle" satırından seçilen kullanıcılar; Phase 1
//     CREATE payload'ında yazılır (aciklama ile aynı faz, firestoreWrite).
//   - Rules: create/owner-update'te ≤20 string'e izinli; etiketlenen kişi
//     yalnız KENDİNİ çıkaran update yapabilir (arrayRemove(kendi uid)).
//   - Kendini etiketleme client'ta engellenir (composer kendi uid'sini
//     listeye almaz; orchestrator da defansif filtreler).
//   - Sorgu (TaggedGridLite kanonik): taggedUids array-contains uid +
//     authorIsPrivate == false + v3_ready == true + orderBy(tarih desc).
//     Composite index: clips(taggedUids CONTAINS, authorIsPrivate ASC,
//     v3_ready ASC, tarih DESC).
//   - Bildirim: publish sonrası her etiketlenene type="tag" notification
//     (notifications/service.js).
export const CLIP_TAG_MAX = 20;

// ─── FAZ 1B GÖREV 2: Günlük view analitiği doc sözleşmesi ─────────────────
// Path: clips/{clipId}/analytics/{YYYY-MM-DD}
// Yazan: aggregateClipViewsDaily (scheduled) + backfillClipViewAggregates (callable)
// Okuyan: yalnızca klip sahibi (creator dashboard).
export const CLIP_DAILY_ANALYTICS_FIELDS = Object.freeze([
  "date",           // "YYYY-MM-DD" (İstanbul takvimi)
  "views",          // viewLog doc sayısı (saatlik dedup'lı)
  "completes",      // ≥%90 tamamlanan view sayısı
  "watchedMsSum",   // toplam izlenme ms
  "avgWatchedMs",   // watchedMsSum / views
  "uniqueViewers",  // distinct uid
  "updatedAt",      // serverTimestamp
]);

// Path: analytics_clips_daily/{YYYY-MM-DD}
// Tüm kliplerin o gün özeti — admin dashboard.
export const CLIPS_GLOBAL_DAILY_FIELDS = Object.freeze([
  "date",
  "totalViews",
  "totalCompletes",
  "totalWatchedMs",
  "avgWatchedMs",
  "uniqueClips",
  "uniqueViewers",
  "topClips",   // [{ clipId, views, completes }] (max 20)
  "updatedAt",
]);

// ─── FAZ 1B GÖREV 3: Engagement ranking skoru ────────────────────────────
// Sunucu (rerankClipsDaily / rerankClipsCallable) engagementScore +
// qualityBucket + lastRankedAt alanlarını yazar. Client yazamaz — zaten
// CLIP_SERVER_ONLY_FIELDS listesinde.
//
// Quality bucket = discrete ölçek; UI'da rozet / filtreleme için kullanılır.
// Bucket eşikleri formulayla birlikte evrilebilir; tek kaynak burası.
export const CLIP_QUALITY_BUCKET = Object.freeze({
  HOT: "hot",        // score >= 5
  RISING: "rising",  // score >= 2
  WARM: "warm",      // score >= 0.5
  COLD: "cold",      // score < 0.5
});

export const CLIP_QUALITY_BUCKET_THRESHOLDS = Object.freeze({
  hot: 5,
  rising: 2,
  warm: 0.5,
});

// Skor hesaplamasında input olan alan isimleri (ranking fonksiyonu bunlarla
// uyumlu çalışmak zorunda).
export const CLIP_RANK_INPUT_FIELDS = Object.freeze([
  "viewCount",
  "completeCount",
  "watchedMsTotal",
  "tarih",
]);

// ─── FAZ 1B GÖREV 5: Moderasyon (SafeSearch) sonuç sözleşmesi ─────────────
// moderationStatus alanı (CLIP_SERVER_ONLY_FIELDS içinde) bu shape ile yazılır.
// Transcoder callback veya moderateClipCallable tarafından yazılır.
export const CLIP_MODERATION_VERDICT = Object.freeze({
  APPROVED: "approved",     // Geçerli, feed'de gösterilebilir
  REJECTED: "rejected",     // Politika ihlali — status=rejected ile beraber
  UNREVIEWED: "unreviewed", // Vision API hatası vb. — fail-open, admin revize edebilir
});

export const CLIP_MODERATION_FIELDS = Object.freeze([
  "verdict",    // "approved" | "rejected" | "unreviewed"
  "reason",     // "adult:VERY_LIKELY" | "violence:LIKELY" | null
  "scores",     // { adult, spoof, medical, violence, racy } (enum strings) | null
  "provider",   // "vision_safesearch_poster_v1"
  "checkedAt",  // serverTimestamp
  "error",      // unreviewed ise hata mesajı
]);

// ─── FAZ 1B GÖREV 6: viewLog retention (TTL purge) ────────────────────────
// clips/{clipId}/viewLog/{uid_hourBucket} doc'ları saatlik dedup için yazılır
// (logClipView). Günlük agregasyon clips/{clipId}/analytics/{YYYY-MM-DD}
// altına özet yazdığı için ham log'lar N gün sonra silinebilir.
// Scheduled function `purgeOldClipViewLogs` her gece çalışır ve
// lastAt < now - RETENTION_DAYS olan log'ları batch delete eder.
// Admin manuel tetikleme için `purgeClipViewLogsCallable` var (dryRun destekli).
export const CLIP_VIEWLOG_RETENTION_DAYS = 30;

// viewLog doc alan sözleşmesi — retention query bu alanlara dayanır.
export const CLIP_VIEWLOG_FIELDS = Object.freeze([
  "uid",        // viewer uid
  "clipId",     // parent clipId (redundant ama collectionGroup query için rahat)
  "firstAt",    // ilk view timestamp (bucket içi)
  "lastAt",     // son view timestamp — retention filter alanı
  "completed",  // bu oturumda ≥%90 izlendi mi
  "watchedMs",  // toplam izlenme ms (bu bucket içinde)
]);

// ─── FAZ 1C GÖREV 1: Yorum sözleşmesi ─────────────────────────────────────
// Tarihsel durum: clip yorumları şu anda `clips/{clipId}.yorumlar` ARRAY'i
// içinde tutuluyor (legacy inline). Bu hem write-amplification (her yorum
// tüm array'i overwrite) hem scale (1MB doc limiti) problemidir.
//
// Hedef: Canonical yazım `content/clip:{clipId}/comments/{commentId}`
// subcollection'ına geçer. Bu path zaten posts için `src/commentsClient.js`
// tarafından kullanılıyor ve Firestore rules `match /content/{contentId}`
// (line 304) + `match .../comments/{commentId}` (line 339) altında tanımlı.
// Yeni bir collection oluşturmak yerine mevcut altyapı tekrar kullanılır.
//
// Migrasyon stratejisi: feature flag ile üç mod
//   - "inline"        → sadece legacy array'e yaz (eski davranış, geri sarma)
//   - "dual"          → hem inline array hem subcollection (güvenli default)
//   - "subcollection" → sadece subcollection (legacy array donduruldu)
//
// Görev 3'te (backfill) tüm eski array yorumları subcollection'a taşınır,
// sonra "subcollection" moduna geçilip array'ler dondurulur.

// Subcollection doc alan sözleşmesi (commentsClient.js ile aynı).
export const CLIP_COMMENT_FIELDS = Object.freeze([
  "text",        // trim'lenmiş yorum metni
  "authorId",    // yorumu yazan uid
  "authorName",  // displayName snapshot
  "authorPhoto", // photoURL snapshot
  "createdAt",   // serverTimestamp — okuma DESC sıralama alanı
]);

// content/clip:{clipId} composite key — commentsClient.js sözleşmesi.
// Bu fonksiyon tek kaynaktır; clip yorum yazan her yer bunu çağırmalı.
export const CLIP_COMMENT_TARGET_TYPE = "clip";
// Gövde string olmayanda FIRLATIYOR → sözleşme "her şey gelebilir".
export function clipCommentContentKey(clipId: unknown): string {
  if (!clipId || typeof clipId !== "string") {
    throw new Error("clipCommentContentKey: clipId zorunlu ve string olmalı.");
  }
  return `${CLIP_COMMENT_TARGET_TYPE}:${clipId}`;
}

// Yorum uzunluk sınırları — UI + sunucu moderasyonu için ortak değer.
export const CLIP_COMMENT_MIN_LENGTH = 1;
export const CLIP_COMMENT_MAX_LENGTH = 2200;

// Legacy inline `yorumlar` array item sözleşmesi — okuyucu tarafın
// subcollection dönene kadar geriye uyumlu çalışabilmesi için.
export const CLIP_COMMENT_LEGACY_FIELDS = Object.freeze([
  "userId",     // authorId karşılığı (eski ad)
  "username",   // authorName karşılığı
  "photoURL",   // authorPhoto karşılığı
  "text",       // aynı
  "timestamp",  // ISO string — createdAt karşılığı
]);

// ─── FAZ 1C GÖREV 3: Inline → subcollection backfill meta ──────────────────
// `backfillClipCommentsCallable` her clip'i işledikten sonra clip doc'a
// idempotentlik ve raporlama alanları yazar. Scheduler yeniden çalışırsa
// commentsBackfilledAt set olanları atlar (force=true dışında).
export const CLIP_COMMENT_BACKFILL_FIELDS = Object.freeze([
  "commentsBackfilledAt",    // serverTimestamp — backfill tamamlandı işareti
  "commentsBackfilledCount", // kopyalanan + skip edilen yorum sayısı
]);

// Subcollection doc içindeki backfill damgası — canonical doc'un backfill
// kaynağından mı geldiğini ayırt eder (Görev 5 moderasyon + doğrulama için).
// Şekli: _legacy: { backfilled: true, sourceCommentId, backfilledAt }
export const CLIP_COMMENT_LEGACY_STAMP_KEY = "_legacy";

// ─── FAZ 1C GÖREV 4: Threading (1-level reply) + replyCount denorm ──────────
// Bağlam: Görev 1-3'te top-level yorumlar `content/clip:{clipId}/comments/{cid}`
// altına taşındı. Görev 4'te yoruma cevap (tek seviye, IG benzeri) eklenir.
//
// Path: `content/{contentId}/comments/{commentId}/replies/{replyId}`
//   - `contentId` = `clip:{clipId}` (veya gelecekteki post/story kullanımı).
//   - Reply'ler sadece 1 seviye derinlik — yanıt'a yanıt UI'da top-level reply
//     olarak ele alınır (reply-to-reply = @mention-as-prefix pattern, Görev 6).
//   - replyId: Firestore auto-id (client addDoc ile üretir).
//
// Denormalization:
//   - Parent comment doc'unda `replyCount` alanı Cloud Function
//     `onContentCommentReplyWrite` (functions/index.js v2) tarafından tutulur.
//   - UI yanıtları açmadan sayıyı rozet olarak gösterebilsin diye.
//   - Rules: client parent comment doc'u update EDEMEZ (allow update: if false);
//     bu yüzden sayaç admin SDK ile sadece trigger'dan yazılır.
//
// Yeni collection/rule yok — aynı `comments` altı subcollection, rules
// `match /comments/{commentId}/replies/{replyId}` bloğu firestore.rules:339+
// bölümüne eklenir.

// Reply doc alan sözleşmesi — tek seviye thread.
export const CLIP_REPLY_FIELDS = Object.freeze([
  "text",           // trim'lenmiş yanıt metni
  "authorId",       // reply yazan uid
  "authorName",     // displayName snapshot
  "authorPhoto",    // photoURL snapshot
  "createdAt",      // serverTimestamp — sıralama alanı (ASC; oldest-first IG benzeri)
  "parentCommentId", // denorm: parent comment id (collectionGroup query kolaylığı)
  // opsiyonel: replyToUserId — "@username kısmı için" (Görev 6 mention)
]);

// Parent comment doc'unda cloud function tarafından tutulan alan(lar).
// Rules client update'e izin vermez; sadece trigger yazar.
export const CLIP_COMMENT_DENORM_FIELDS = Object.freeze([
  "replyCount",            // 0+ — aktif (soft-delete edilmemiş) reply sayısı
  "replyCountUpdatedAt",   // serverTimestamp — son senkron zamanı
]);

// Reply uzunluk sınırları — top-level yorumla aynı (tutarlılık).
export const CLIP_REPLY_MIN_LENGTH = 1;
export const CLIP_REPLY_MAX_LENGTH = 2200;

// Reply default sayfalama büyüklüğü — UI "daha fazla yanıt" expand'i için.
export const CLIP_REPLY_DEFAULT_PAGE = 10;
export const CLIP_REPLY_MAX_PAGE = 50;

// ─── FAZ 1C GÖREV 5: Yorum moderasyonu (NL API, fail-open) ─────────────────
// Bağlam: Görev 1-4 top-level yorum + tek seviye reply subcollection'a
// taşındı. Görev 5 Cloud Natural Language API `moderateText` ile yazılan
// metni tarar ve toxic/insult/profanity/sexual içeriği gizler.
//
// Davranış — fail-open:
//   - NL API hatası → verdict="unreviewed", yorum görünür kalır (block'lanmaz).
//   - Admin callable ile tekrar tarama yapılabilir.
//
// Rejection → "hidden" soft-delete:
//   - Rejected yorum `hidden: true` alır. `functions/index.js`
//     `isSoftDeletedComment` helper'ı bu alanı zaten tanır; commentsCount
//     otomatik decrement edilir (aynı bağışıklık reply trigger'ında).
//   - UI list API bu alanları görünür kılıp filtre kararını client'a bırakır.
//
// Policy eşikleri:
//   - NL moderateText 0..1 confidence döner. Her kategori için eşik altında
//     yorum onaylanır; eşik üstü red sebebiyle reason alanına yazılır.
//   - Eşikler `MODERATION_THRESHOLDS` sabitinde (sunucuda); burada tüketici
//     UI tarafı için sadece verdict enum'ı + kategori isimleri paylaşılır.
//
// Backfill damgası etkileşimi:
//   - `_legacy.backfilled === true` olan yorumlar eski inline array'den
//     taşındı; bunlar moderasyon'dan geçmedi. Admin callable'a
//     `reScanLegacyOnly: true` argümanıyla sadece damgalı doc'lar taranır.

export const CLIP_COMMENT_MODERATION_VERDICT = Object.freeze({
  APPROVED: "approved",     // Eşiklerin altında — görünür
  REJECTED: "rejected",     // Eşik üstü — hidden:true yazılır
  UNREVIEWED: "unreviewed", // API hatası → fail-open, admin revize edebilir
});

// moderationStatus alt alanları (comment + reply doc'larında aynı shape).
export const CLIP_COMMENT_MODERATION_FIELDS = Object.freeze([
  "verdict",   // "approved" | "rejected" | "unreviewed"
  "reason",    // "toxic:0.84" | "sexual:0.91" | null
  "scores",    // { Toxic: 0.12, Insult: 0.03, ... } | null (raw NL API)
  "provider",  // "nl_moderate_text_v1"
  "checkedAt", // serverTimestamp
  "error",     // unreviewed ise hata mesajı (ilk 500 karakter)
]);

// UI filtering hint — rejected veya unreviewed durumda yorumun soft-delete
// alanı. `functions/index.js` commentsCount counter bunu da "soft-deleted"
// sayar (isSoftDeletedComment → data.hidden truthy).
export const CLIP_COMMENT_HIDDEN_FIELD = "hidden";

// ─── FAZ 1C GÖREV 6: In-app bildirim altyapısı (notifications collection) ───
// Bağlam: Projede zaten `/notifications/{id}` collection + Firestore rules
// hazır (to, from, read, createdAt, type, text, postId, fromUsername,
// fromAvatar). Feed.js "like" notifikasyonu yazıyor. Bu görevde:
//   - Tek kaynak type enum + dedup id stratejisi
//   - Clip yorum/yanıt için notification tetikleyiciler (client-side writer)
//   - Bildirimler.js'te type-aware rendering + deep-link + auto mark-read
//
// Dedup ID pattern — deterministik:
//   - like:    `like_{contentId}_{fromUid}`          (re-like overwrite; rules)
//   - comment: `comment_{clipId}_{commentId}`         (commentId zaten unique)
//   - reply:   `reply_{clipId}_{commentId}_{replyId}` (global unique)
//   - mention: `mention_{clipId}_{commentId}_{fromUid}` (Görev 7'de aktifleşir)
//   - follow:  `follow_{toUid}_{fromUid}`             (bir takip = bir notif)
//
// Not: Firestore rules update'te yalnızca `read` değiştirilebilir. Same-key
// create teknik olarak `set()` ile mümkündür (rules `allow create` check'i
// `request.resource.data.read == false` üzerinden geçtiği için aynı doc'u
// yeniden "create" gibi ele alıp eski bildirimi taze içerikle değiştirir).
// Pratikte same-key + same-from kombinasyonu olan doc sahibi zaten bildirim
// sahibidir — spam önlenmiş olur.
//
// Deep-link: notification doc'unda `link` alanı opsiyonel route string
// tutulur (örn `/#clip/{clipId}` ya da `/clip/{clipId}?cid={commentId}`).
// Bildirimler.js tıklanınca window.location.hash set eder.

export const NOTIFICATION_TYPE = Object.freeze({
  LIKE: "like",
  COMMENT: "comment",
  REPLY: "reply",
  MENTION: "mention",
  FOLLOW: "follow",
  // FAZ 5 P1-2 — gizli hesaba gelen takip isteği (server trigger yazar:
  // functions/src/follow_requests.ts onFollowRequestCreateV3).
  FOLLOW_REQUEST: "follow_request",
  // DM — yeni mesaj (server trigger yazar: functions/src/dm_message_push.ts
  // onDmMessageCreatePush). link: /direct/t/{conversationId}.
  DM: "dm",
});

// Notification doc alan sözleşmesi — rules create check'iyle uyumlu.
// `type`, `contentId`, `contentType`, `link` opsiyonel ama UI tarafta olması
// beklenir; legacy notifikasyonlar (type alanı olmayan) sadece text gösterir.
export const NOTIFICATION_FIELDS = Object.freeze([
  "to",           // recipient uid (rules required)
  "from",         // sender uid (rules required = auth.uid)
  "read",         // false on create (rules required)
  "createdAt",    // serverTimestamp
  "type",         // NOTIFICATION_TYPE değerlerinden
  "text",         // "klibini beğendi." / "klibine yorum yaptı." / ...
  "fromUsername", // sender displayName snapshot
  "fromAvatar",   // sender photoURL snapshot
  "contentId",    // clip/post id
  "contentType",  // "clip" | "post" | "story"
  "commentId",    // yorum/yanıt bildirimleri için parent comment id
  "replyId",      // reply bildirimleri için reply id
  "preview",      // yorumun ilk 80 karakteri (UI preview)
  "link",         // deep-link hash/path (optional)
]);

// Preview kısaltma sınırı — Firestore rule'ları text uzunluğunu sınırlamaz,
// ama UI okuma performansı için server-side'da da kesmek iyidir.
export const NOTIFICATION_PREVIEW_MAX_LENGTH = 80;

// ─── FAZ C — Multi-bitrate adaptive streaming (yatırımcı şartı #2) ─────────
//
// V3 klipler eskiden tek progressive MP4 (R2'den) servis ediliyordu.
// 3G/yavaş hat kullanıcıları buffer cehennemi yaşıyordu. FAZ C ile
// transcoder pipeline her klip için 3 variant üretir:
//
//   240p / 480p / 720p (vertical 9:16 native; `res` = genişlik)
//   bitrate: 250k / 600k / 1.5M (sırasıyla) — P0.4: tek kaynak shared/ladder.ts
//
// Upscale yok: kaynak 720p ise sadece 480p+720p variant üretilir; kaynak
// 480p ise sadece 480p kalır (özgün dosya = variant). Bu gereksiz
// transcoding maliyetini önler.
//
// Schema:
//   clips/{id}.v3_variants = [
//     { res: 480, bitrate: 600000, url: "https://r2.../variant_480.mp4",
//       sizeBytes: 1234567, durationMs: 18500, codec: "h264" },
//     { res: 720, bitrate: 1500000, url: "...", sizeBytes: ..., ... },
//     { res: 1080, bitrate: 4000000, url: "...", sizeBytes: ..., ... },
//   ]
//
// Client (useVariantSelector) navigator.connection.downlink okuyup
// seçer. Manuel override: localStorage["mlv3.player.quality"] ∈
// {"auto","480","720","1080"}.
//
// Backward compat: v3_variants alanı yoksa player legacy v3_r2Url
// (tek MP4) kullanır. Backfill scheduled (myclips_v3_variants_backfill)
// haftalık 200 klip işler; field eklenmiş eski klipler için.

// PR-V1 (2026-07-06): 240p instant rung — transcoder LADDER'ı ile senkron.
// V3 HLS hattında v3_variants[].url {res}p/index.m3u8 rendition playlist'leri
// taşır; 240p, startLevel:0 invariant'ının seçtiği küçük ilk-fragment tabanıdır.
// useVariantSelector defansif (bilinmeyen/eksik res crash üretmez): 240'sız
// eski klipler ve 240'lı yeni klipler aynı kodla çalışır.
//
// P0.4 (2026-09-05): ÜÇ KOPYALI ELLE-SENKRON BİTTİ. Buradaki liste eskiden
// 1080'i içeriyordu ve sunucu 1080 HİÇ üretmiyordu (ölçüldü). Artık tek
// kaynak `myclipsv3/shared/ladder.ts` (functions + Cloud Run aynaları AST
// sözleşme testiyle eşitlenir). Aşağıdaki sabitler ORADAN türetilir.
export const CLIP_VARIANT_RESOLUTIONS: readonly number[] = Object.freeze(
  LADDER.map((r) => r.res),
);

// Bitrate ladder (bps) — `v3_variants[].bitrate` birimi. Transcoder aynı
// sözleşmeden okur (`bitrateKbps * 1000`).
export const CLIP_VARIANT_BITRATES: Readonly<Record<number, number>> = Object.freeze(
  Object.fromEntries(LADDER.map((r) => [r.res, r.bitrateKbps * 1000])),
);

// Variant doc şekli — client (useVariantSelector) ve server (transcoder)
// aynı sözleşmeye uyar.
export const CLIP_VARIANT_FIELDS = Object.freeze([
  "res",        // 480 | 720 | 1080
  "bitrate",    // bps
  "url",        // public R2 URL
  "sizeBytes",  // dosya boyutu (UI'da göstermek için opsiyonel)
  "codec",      // "h264" | "h265" — ileride h265 fallback için
  "durationMs", // transcoded duration (kaynak ile eşit normalde)
]);

// Client variant selector kill-switch + manual override anahtarı.
// "off" → hook her zaman legacy v3_r2Url'e düşer (variant logic devre dışı).
// "auto" / ladder'ın `secilebilir` kademeleri ("480" / "720") → manuel seçim.
// P0.4: "1080" listeden ÇIKTI — hiçbir klipte üretilmiyordu; menüde
// "Mevcut değil" diye asılı duruyordu.
export const CLIP_QUALITY_STORAGE_KEY = "mlv3.player.quality";
export const CLIP_QUALITY_VALUES: readonly string[] = Object.freeze([
  "auto",
  ...MANUEL_KALITE_DEGERLERI,
  "off",
]);

// ─── FAZ E — Mahalle Modu (Niş #1, hyperlocal feed) ────────────────────────
//
// V3 ranker (rankPolicyV3.computeLocationBoost) zaten
// `users/{uid}.v3_homeCity` + `clips/{id}.v3_authorCity` field'larını
// okuyup boost uyguluyor (defansif: alanlar yoksa no-op). FAZ E ile
// CAPTURE KATMANI yerleştirilir + dedicated "Mahalle" feed tab eklenir.
//
// Capture pattern:
//   1. Kullanıcı ProfilDuzenle.js'te "Konumumu Belirle" → geolocation
//      izni → reverseGeocode → users/{uid}.merge({
//        v3_homeCity, v3_homeCountry, v3_homeCountryCode,
//        v3_homeGeohash (geohash precision 6 ≈ 1.2km cell),
//        v3_homeUpdatedAt
//      })
//   2. Klip yükleyince firestoreWrite finalizeClipDocPhase2 kullanıcının
//      home location'ını klibe denormalize eder:
//        clips/{id}.merge({
//          v3_authorCity, v3_authorCountry, v3_authorCountryCode,
//          v3_authorGeohash, v3_authorLocationUpdatedAt
//        })
//   3. Mahalle tab subscribeToNeighborhoodClips({ lat, lon, radiusKm })
//      geofire-common geohashQueryBounds kullanarak Firestore range
//      query yapar; client-side distance filter ile false-positive'leri
//      temizler.
//
// Privacy / KVKK uyumu:
//   - Exact lat/lon ASLA expose edilmez. Sadece geohash precision 5-6
//     (cell ≈ 4.9km × 4.9km — 600m × 1.2km arası) saklanır.
//   - Şehir/ülke bilgisi normalleştirilmiş string (Turkish-aware
//     locationBoost.normalizeLocationToken).
//   - Opt-in flow zorunlu: LocationOptInSheet açık onay almadan asla
//     yazma yapmaz. Kullanıcı reddederse `v3_homeOptOut: true` flag set
//     edilir, tekrar sorulmaz.
//   - Konum kaldırma: profile'dan "konumu sil" tek tıkla mümkün
//     (FieldValue.delete() ile alanlar kaldırılır).
//
// Geohash precision tradeoff:
//   - Precision 7 (152m × 152m) → çok hassas, KVKK risk
//   - Precision 6 (1.2km × 600m) → semt seviyesi (TR mahalle ortalama 5-15km²)
//   - Precision 5 (4.9km × 4.9km) → ilçe seviyesi
//   - Precision 4 (39km × 19km) → şehir seviyesi
//   Seçim: precision 6 (semt). Mahalle feed radius 500m-25km arası
//   slider; geohashQueryBounds bound'ları otomatik ayarlar.
export const USER_HOME_LOCATION_FIELDS = Object.freeze([
  "v3_homeCity",          // Bayesian-normalized lowercase city (locationBoost ile aynı)
  "v3_homeCountry",       // Full country name
  "v3_homeCountryCode",   // ISO-2 lowercase
  "v3_homeGeohash",       // geofire-common precision 6
  "v3_homeUpdatedAt",     // serverTimestamp
  "v3_homeOptOut",        // boolean — kullanıcı opt-in'i reddetti
]);

export const CLIP_AUTHOR_LOCATION_FIELDS = Object.freeze([
  "v3_authorCity",
  "v3_authorCountry",
  "v3_authorCountryCode",
  "v3_authorGeohash",
  "v3_authorLocationUpdatedAt",
]);

// Geohash precision — LocationOptInSheet `users/{uid}.v3_homeGeohash`
// yazımında kullanılır.
//
// FAZ 36 (Audit2-E): 6 → 7'ye yükseltildi. ProfilDuzenle (city centroid
// fallback) ve klip yazımı zaten precision 7 kullanıyordu; LocationOptInSheet
// p6 yazınca tutarsızlık oluşuyordu (eski p6 vs yeni p7 entry'ler).
// p7 = ~153m × 153m cell; mahalle granülaritesinde. Mevcut p6 entry'ler
// korunur (Firestore string range query prefix match ile her ikisini yakalar).
export const LOCATION_GEOHASH_PRECISION = 7;

// FAZ F — Yeni Mahalle algoritması. Klip-başına stored precision 7
// (~153m × 153m cell). Mahalle çekirdeği gerçekten "mahalle" granülaritesinde
// olur; cell-edge clipping minimuma iner. Mahalle sorgusu geofire-common
// `geohashQueryBounds([lat,lon], radiusM)` ile bound üretir → stored precision
// alt sınırdır, sorgu radius'üne göre 4-9 bound çıkar. p6 (eski) ve p7 (yeni)
// klipler aynı sorguda karışık çalışır (Firestore string range query precision-
// agnostic).
export const NEIGHBORHOOD_CLIP_GEOHASH_PRECISION = 7;

// Mahalle tab radius preset'leri (km) — UI RadiusSlider seçenekleri.
// Default 5km: TR ortalama bir ilçenin yarıçapı; kullanıcının "yakındaki"
// algısına en yakın karşılık.
export const NEIGHBORHOOD_RADIUS_PRESETS_KM = Object.freeze([0.5, 1, 5, 25]);
export const NEIGHBORHOOD_DEFAULT_RADIUS_KM = 5;

// FAZ F — Klip konum kaynak enum + ranker weight haritası.
// Source weight ranker'da spatialRelevance multiplier'ı olarak çarpılır
// (rankPolicyV3 entegrasyonu sonra; client weight haritası şimdilik
// neighborhood query result'larını client-side sort/score için kullanılabilir).
export const CLIP_LOCATION_SOURCES = Object.freeze({
  GPS: "gps",           // Upload anında browser geolocation (en güvenilir)
  MANUAL: "manual",     // Kullanıcı LocationPickerSheet'ten elle seçti
  PROFILE: "profile",   // users/{uid}.v3_homeGeohash fallback
  PROFILE_BACKFILL: "profile_backfill", // Backfill function ile dolduruldu
});

export const CLIP_LOCATION_SOURCE_WEIGHTS = Object.freeze({
  gps: 1.0,
  manual: 0.85,
  profile: 0.55,
  profile_backfill: 0.55 * 0.7, // ~0.385 — eski klipler GPS'lilerin önüne geçemez
});

// FAZ F — Klip dokümanında konum alanları:
//   FINAL (server-only, neighborhood_validate Cloud Function admin SDK yazar):
//     v3_authorGeohash             string (precision 7 yeni / 6 eski mix)
//     v3_authorGeohashSource       'gps'|'manual'|'profile'|'profile_backfill'
//     v3_authorGeohashAccuracyM    number (yalnız 'gps')
//     v3_authorGeohashCapturedAt   Timestamp (server-trusted)
//     v3_authorLocationUpdatedAt   Timestamp
//
//   CLAIM (client-writable Phase 2, server trigger sonrası DELETE edilir):
//     v3_authorGeohashSourceClaim  'gps'|'manual'|'profile'|'declined'
//     v3_authorLatClaim            number (lat — yalnız gps/manual; geçici)
//     v3_authorLonClaim            number (lon — geçici)
//     v3_authorGeohashAccuracyMClaim  number (yalnız gps)
//     v3_authorGeohashCapturedAtMsClaim  number (Date.now())
//
// Privacy: lat/lon ASLA kalıcı saklanmaz — server trigger geohash
// hesapladıktan sonra claim alanlarını FieldValue.delete() ile siler.
export const CLIP_AUTHOR_GEOHASH_FINAL_FIELDS = Object.freeze([
  "v3_authorGeohash",
  "v3_authorGeohashSource",
  "v3_authorGeohashAccuracyM",
  "v3_authorGeohashCapturedAt",
]);

export const CLIP_AUTHOR_GEOHASH_CLAIM_FIELDS = Object.freeze([
  "v3_authorGeohashSourceClaim",
  "v3_authorLatClaim",
  "v3_authorLonClaim",
  "v3_authorGeohashAccuracyMClaim",
  "v3_authorGeohashCapturedAtMsClaim",
]);

// FAZ A — Konum doğrulaması başarısız olan klipler için reject reason enum.
// Server trigger (onClipReadyValidateLocation) hard-reject yaptığında klip
// doc'una v3_ready=false + v3_authorGeohashRejectReason yazar; UI bu
// alanı okuyup yükleyiciye yumuşak sebep göstersin (Studio kart rozeti).
//
// Reject reason → kullanıcı dostu metin haritası UI tarafında (Studio).
// Burada yalnız enum + alan adı tek kaynak; haritayı UI bir kez tanımlar.
export const CLIP_LOCATION_REJECT_REASONS = Object.freeze({
  OUT_OF_BBOX: "out_of_bbox",         // TR sınırı dışında lat/lon claim
  LOW_ACCURACY: "low_accuracy",       // GPS accuracy > 500m
  TELEPORT: "teleport",               // Önceki klipten >1000km/h hareket
  DRIFT_HARD_REJECT: "drift_hard_reject", // Son 20 klip içinde >200km drift
});

export const CLIP_LOCATION_REJECT_FIELD = "v3_authorGeohashRejectReason";

// FAZ F — Mahalle query distance filter. geohashQueryBounds false-positive
// üretir (radius'tan büyük cell çekebilir) — distance check şart. Lat/lon
// klipte saklanmadığı için cell-merkezi (geohashDecode) üzerinden
// approximate distance hesaplanır. Tolerance: gerçek radius * 1.5
// (cell merkez sapması p7 için ~110m, p6 için ~600m).
export const NEIGHBORHOOD_DISTANCE_TOLERANCE_FACTOR = 1.5;

// Opt-in modal kill-switch + state localStorage anahtarları.
// "skipped" → kullanıcı modali kapattı ama henüz reddetmedi
//   (sonraki açılışta tekrar göster); reddederse server'a v3_homeOptOut: true.
// "completed" → location set edildi (tekrar sorma).
// Boş → ilk kez modal göster.
export const LOCATION_OPTIN_STORAGE_KEY = "mlv3.location.optin";
export const LOCATION_OPTIN_VALUES = Object.freeze(["skipped", "completed", "denied"]);

/* ───────────────  FAZ II — F2.1 Onboarding Topic Picker  ─────────────── */
// 5-ajan denetimi bulgusu: Yeni kullanıcı cold-start tek yönlü (corpus<1000
// → ranking BYPASS) çalışıyor; kullanıcı kendisi ne istediğini söyleyemiyor,
// algoritma sıfır prior'la başlıyor. Instagram'da onboarding interest
// selection 2020'den beri var (5+ topic seçimi → ilk feed kalibre); MyClips
// bu retention katmanı eksikti.
//
// Topic id'leri = base hashtag adları. rankPolicyV3.computeOnboardingTopicBoost
// clip.hashtags array'inde kullanıcının seçtiği topic id'lerinden biri
// varsa boost uygular. Yeni hashtag mapping tablosu YOK (basit ve
// genişletilebilir; ileride F2.x'te hashtag synonym map eklenebilir).
//
// Topic listesi Türkiye odaklı — yaygın kategoriler + lokal kimlikle uyumlu
// "yerel" + Türkiye-özgü "gundem" (haber/civic; FAZ III tartışmasında
// gündeme gelen civic media kanalı için ön hazırlık).
export const ONBOARDING_STORAGE_KEY = "mlv3.onboarding.v1";
// Storage değerleri: "completed" (interests seçildi) | "skipped" (kullanıcı
// kapadı; sonraki açılışta tekrar göster) | "denied" (kapsam dışı şu an;
// gelecekte "tekrar gösterme" seçeneği için ayrılmış). Boş → ilk kez modal.
export const ONBOARDING_VALUES = Object.freeze(["completed", "skipped", "denied"]);

// 12 topic — Türkiye odaklı. Sırlama frekans değil kategorik (UI'de grid
// olarak gösterilir; kullanıcı görsel olarak tarayabilsin).
export const ONBOARDING_TOPICS = Object.freeze([
  { id: "yemek", label: "Yemek", emoji: "🍳" },
  { id: "spor", label: "Spor", emoji: "⚽" },
  { id: "muzik", label: "Müzik", emoji: "🎵" },
  { id: "mizah", label: "Mizah", emoji: "😄" },
  { id: "doga", label: "Doğa", emoji: "🌿" },
  { id: "egitim", label: "Eğitim", emoji: "📚" },
  { id: "gundem", label: "Gündem", emoji: "📰" },
  { id: "sanat", label: "Sanat", emoji: "🎨" },
  { id: "oyun", label: "Oyun", emoji: "🎮" },
  { id: "yasam", label: "Yaşam", emoji: "✨" },
  { id: "teknoloji", label: "Teknoloji", emoji: "💻" },
  { id: "yerel", label: "Yerel", emoji: "📍" },
]);
// Yardımcı helper'lar — UI ve hook yerine merkezi.
export const ONBOARDING_MIN_INTERESTS = 3;
export const ONBOARDING_MAX_INTERESTS = 6;
// Bootstrap counter localStorage anahtarı; onboarding tamamlandıktan
// sonraki klip görüntülenme sayısı. Limit dolunca boost no-op.
export const ONBOARDING_VIEW_COUNTER_KEY = "mlv3.onboarding.viewsSinceCompletion";
// Geçerli bir topic id mi? (UI ve ranker defansif kontrolü için)
const _ONBOARDING_TOPIC_IDS = new Set(ONBOARDING_TOPICS.map((t) => t.id));
export function isValidOnboardingTopicId(id: unknown): boolean {
  return typeof id === "string" && _ONBOARDING_TOPIC_IDS.has(id);
}

// ─── FAZ F — Kanıtlı Klip / Authenticity Badge (Niş #2) ────────────────────
//
// Reels/TikTok'un YAPMADIĞI niş özellik. Yükleme anında orijinal blob'un
// SHA-256 hash'i + server-trusted timestamp Firestore'a yazılır. Sonradan
// klipte herhangi bir manipülasyon (deepfake, ses değiştirme, splice)
// hash'i değiştirir → orijinal kanıtlanır.
//
// Toggle DEFAULT ON (yatırımcı kararı, yaygın kullanım için):
//   - Yeni yüklenen her klip otomatik kanıtlı.
//   - Composer'da "✓ Kanıtlı klip yap" toggle'ı kullanıcı isterse OFF
//     yapabilir (privacy concern, hızlı yükleme).
//   - localStorage["mlv3.feature.authBadge"] === "off" → tüm authenticity
//     altyapısı devre dışı (composer toggle gizli, feed badge gizli).
//
// Hash hedefi: ham (raw) yüklenen blob. Transcoded variant'lar farklı hash
// üretir; v3_authHashSource alanı hangi versiyonun hash'lendiğini belirtir.
// MVP'de sadece "raw" — kullanıcının yüklediği orijinal dosya. Phase 2'de
// "transcoded" hash de eklenebilir (variant doğrulama için).
//
// Algoritma: SHA-256 (browser native crypto.subtle.digest, hardware-
// accelerated). 50MB klip ≤ 300ms modern cihazlarda. Future-proof: algo
// alanı v3_authHashAlgo'da saklanır → blake3 / SHA-3 geçişi mümkün.
//
// Trusted clock: serverTimestamp() — client clock manipülasyonuna karşı.
// "Bu klip 7 May 2026'da yüklendi" iddiası Firebase'in saat doğruluğuna
// dayanır.
//
// MVP scope:
//   - Hash + timestamp (single-clip authenticity)
//   - Badge UI (feed + embed)
//   - Proof sheet (kullanıcı 3rd-party tool ile re-verify edebilir)
//
// Phase 2 (post-launch, BU COMMIT'TE YOK):
//   - Daily Merkle root anchor (auth_anchors/{date} doc)
//   - On-chain Polygon timestamp (basın değeri için)
//   - Server-side hash re-compute (Cloud Run worker, mismatch ise rozet kaldır)

export const CLIP_AUTH_FIELDS = Object.freeze([
  "v3_authHash",        // 64-char hex SHA-256 (lowercase)
  "v3_authHashAt",      // serverTimestamp — trusted clock
  "v3_authHashAlgo",    // "sha256" (future-proof; "blake3" geçişi için)
  "v3_authHashSource",  // "raw" | "transcoded" — hangi blob'un hash'i
]);

export const CLIP_AUTH_HASH_LENGTH = 64; // SHA-256 hex string length
export const CLIP_AUTH_ALGO = "sha256";
export const CLIP_AUTH_SOURCE_RAW = "raw";
export const CLIP_AUTH_SOURCE_TRANSCODED = "transcoded";

// Composer toggle localStorage anahtarı — kullanıcı bir kez OFF yaptıysa
// sonraki yüklemeler de OFF default'lansın (sticky preference).
export const CLIP_AUTH_COMPOSER_PREF_KEY = "mlv3.compose.authToggle";

// Feature flag — emergency global disable. localStorage["mlv3.feature.authBadge"]
// === "off" → composer toggle gizli + feed badge gizli + embed badge gizli.
export const CLIP_AUTH_FEATURE_FLAG_KEY = "mlv3.feature.authBadge";
